import {
  type CompanyData,
  type ExceptionWithInteractions,
  type ParsedSession,
  type SessionLog,
} from "@/utils/types";
import React, {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Paper,
  ThemeProvider,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";
import ReasoningAndCommands from "../../components/reasoningAndCommands";
import { styled as materialStyled } from "@mui/material/styles";
import styled from "@emotion/styled";
import { useMutation, useQuery } from "@tanstack/react-query";
import { annotateScreenshot, imageUrlToBase64 } from "@/utils/helpers";
import { DateTime } from "luxon";
import CropScreenshotDialog, {
  CropScreenshotDialogStateAtom,
} from "../../components/cropScreenshotDialog";
import { AuthContext } from "@/auth/AuthContext";
import { useAtomValue, useSetAtom } from "jotai";
import { companyDataAtom } from "../test-run/test-run-atoms";
import { CameraAlt, FileCopy } from "@mui/icons-material";
import {
  LogDetailDialog,
  logDetailDialogStateAtom,
} from "@/components/test-editor-gpt/execution-logs/logDetailDialog";
import { darkTheme } from "@/themes";
import { doc, updateDoc } from "firebase/firestore";
import { customFirestore } from "@/firebase";
import { useSearchParams } from "react-router-dom";
import {
  ExceptionCode,
  ExceptionLabel,
  ExceptionMessage,
  HealedLocatorLine,
  StackTraceContainer,
  StackTraceContent,
  StackTraceHeader,
  StackTraceLine,
} from "@/routes/sessions/styled-components";
import { customAlertStateAtom } from "@/components/custom-alert";
import axios from "axios";
import { FASTAPI_SERVER_URL } from "@/constants/aws-constants";
import {
  isAssertionStep,
  isCheckStep,
  parseAssertionConditions,
} from "@/routes/sessions/assertions";

// Assertions aren't tagged with an attempt number server-side: an SDK retry
// (up to 3 attempts) simply re-sends the same assertion and lands as another
// consecutive SessionLog with an identical prompt. Detect retries by looking
// backwards for a run of identical, consecutive assertion prompts.
const getAssertionRetryNumber = (logs: SessionLog[], index: number): number => {
  const current = logs[index];
  if (!isAssertionStep(current)) return 0;
  let retryNumber = 0;
  for (let i = index - 1; i >= 0; i--) {
    if (isAssertionStep(logs[i]) && logs[i].prompt === current.prompt) {
      retryNumber++;
    } else {
      break;
    }
  }
  return retryNumber;
};

const Content = ({ session }: { session: ParsedSession }) => {
  const { user } = useContext<any>(AuthContext);
  const [searchParams] = useSearchParams();
  const exceptionId = searchParams.get("exception");
  const exceptionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const hasScrolled = useRef(false);

  const { data: companyData } = useAtomValue(companyDataAtom(user.companyId));

  const parsedEnvVars = useMemo(
    () =>
      companyData?.settings.envVars?.map((envVar) => `env.${envVar.key}`) ?? [],
    [companyData?.settings]
  );

  const acceptedDynamicValues = useMemo(
    () => [
      "timestamp",
      "currentDate",
      "currentDateFormatted",
      ...parsedEnvVars,
    ],
    [parsedEnvVars]
  );

  // Each non-exception SessionLog is rendered as its own single-item group
  // (see useSessionData in session.tsx), so retries of the same assertion
  // never land in the same group's interactionLogs array. Detect them by
  // flattening back to the full chronological order across all groups.
  const assertionRetryNumbers = useMemo(() => {
    const flatLogs = session.logs.flatMap((group) => group.interactionLogs ?? []);
    const map = new Map<SessionLog, number>();
    flatLogs.forEach((log, index) => {
      map.set(log, getAssertionRetryNumber(flatLogs, index));
    });
    return map;
  }, [session.logs]);

  // Scroll to exception on first render
  useEffect(() => {
    if (exceptionId && !hasScrolled.current) {
      const element = exceptionRefs.current[exceptionId];
      if (element) {
        // Add a small delay to ensure the DOM is fully rendered
        setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          // Optional: Add a highlight effect
          element.style.transition = "box-shadow 0.3s ease";
          element.style.boxShadow = "0 0 20px rgba(76, 99, 255, 0.5)";
          setTimeout(() => {
            element.style.boxShadow = "";
          }, 2000);
        }, 100);
        hasScrolled.current = true;
      }
    }
  }, [exceptionId, session.logs]);

  return (
    <ContentWrapper>
      {companyData != null && (
        <ThemeProvider theme={darkTheme}>
          <LogDetailDialog
            companyData={companyData}
            acceptedDynamicValues={acceptedDynamicValues}
            platform={session.deviceConfig.platform}
          />
        </ThemeProvider>
      )}
      <CropScreenshotDialog />
      {session.logs.map((log, parentIndex) => {
        return log.exception ? (
          <ExceptionWrapper
            key={parentIndex}
            exceptionWithInteractions={log}
            ref={(el) => {
              if (log.exception && el) {
                exceptionRefs.current[log.exception.id] = el;
              }
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              {log.interactionLogs?.map((interactionLog, index) => (
                <LogItem
                  key={index}
                  log={interactionLog}
                  companyData={companyData!}
                  session={session}
                  healedLocator={
                    index === log.interactionLogs.length - 1
                      ? log.exception?.healedLocator
                      : undefined
                  }
                  assertionRetryNumber={assertionRetryNumbers.get(interactionLog)}
                />
              ))}
            </div>
          </ExceptionWrapper>
        ) : (
          log.interactionLogs?.map((interactionLog, index) => (
            <LogItem
              key={index}
              log={interactionLog}
              companyData={companyData!}
              session={session}
              assertionRetryNumber={assertionRetryNumbers.get(interactionLog)}
            />
          ))
        );
      })}
    </ContentWrapper>
  );
};

// Update ExceptionWrapper to use forwardRef
const ExceptionWrapper = React.forwardRef<
  HTMLDivElement,
  {
    exceptionWithInteractions: ExceptionWithInteractions;
    children: ReactNode;
  }
>(({ exceptionWithInteractions, children }, ref) => {
  const handleCopy = useCallback(async (text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  return (
    <Paper
      ref={ref}
      sx={{
        padding: "2rem",
        backgroundColor: "rgb(255, 240, 240)",
      }}
      elevation={0}
    >
      <StackTraceContainer>
        <StackTraceHeader>
          <ExceptionLabel>Exception</ExceptionLabel>
          <ExceptionCode>
            {exceptionWithInteractions.exception?.type}
          </ExceptionCode>
        </StackTraceHeader>
        <StackTraceContent>
          <StackTraceLine>
            <span className="at">at</span>
            <span className="class-path">
              {exceptionWithInteractions.exception?.class}.
            </span>
            <span className="method">
              {exceptionWithInteractions.exception?.method}
            </span>
            <span className="location">
              ({exceptionWithInteractions.exception?.filename}:
              <span className="line-number">
                {exceptionWithInteractions.exception?.line}
              </span>
              )
            </span>
          </StackTraceLine>
        </StackTraceContent>
      </StackTraceContainer>

      {children}
    </Paper>
  );
});

const LogItem = ({
  log,
  companyData,
  session,
  healedLocator,
  assertionRetryNumber,
}: {
  log: SessionLog;
  companyData: CompanyData;
  session: ParsedSession;
  healedLocator?: string;
  assertionRetryNumber?: number;
}) => {
  const assertionConditions = useMemo(() => parseAssertionConditions(log), [log]);
  const setLogDetailDialogState = useSetAtom(logDetailDialogStateAtom);
  const [updating, setUpdating] = useState(false);
  const [disabledForCaching, setDisabledForCaching] = useState(
    log.disabledForCaching ?? false
  );

  const isFailedSession = session.status === "failed";
  const isFromCache = log.fromCache;
  const isCheckboxDisabled = isFailedSession || isFromCache || updating;
  const isChecked =
    isFailedSession || isFromCache ? false : !disabledForCaching;

  const getTooltipMessage = () => {
    if (isFailedSession) return "Failed sessions are not used for caching";
    if (isFromCache) return "This action is already cached";
    return "";
  };

  const { user } = useContext<any>(AuthContext);

  const annotatedBaselineData = useQuery({
    queryKey: ["annotateSessionScreenshot", log.screenshotUrl],
    queryFn: async () => {
      try {
        const image = await imageUrlToBase64(log.screenshotUrl);
        const nonAnnotatedScreenshot = image as string;

        const annotatedImage = await annotateScreenshot(
          nonAnnotatedScreenshot,
          (log.appetizeCommands?.at(0) ?? "").split("\n")
        );

        return { annotatedImage, nonAnnotatedScreenshot };
      } catch (e) {
        console.log(e);
      }
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const clickOnLogHandler = useCallback(
    (log: SessionLog) => {
      setLogDetailDialogState({
        open: true,
        isBaseline: false,
        redefiningStep: true,
        sessionLog: {
          ...log,
          id: Date.now(),
          screenshotUrl:
            annotatedBaselineData.data?.annotatedImage ?? log.screenshotUrl,
        },
      });
    },
    [annotatedBaselineData.data]
  );

  const setCropScreenshotDialogState = useSetAtom(
    CropScreenshotDialogStateAtom
  );

  const setScreenshot = useCallback(() => {
    setCropScreenshotDialogState({
      open: true,
      imageSrc: annotatedBaselineData.data!.nonAnnotatedScreenshot,
      companyData,
      deviceName: session.deviceConfig.device ?? "--",
    });
  }, [
    annotatedBaselineData.data?.nonAnnotatedScreenshot,
    session,
    companyData,
  ]);

  const promptActionHandler = useCallback(() => {
    clickOnLogHandler(log);
  }, [log]);

  const handleCheckboxChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    const newDisabledForCaching = !checked;
    setUpdating(true);
    setDisabledForCaching(newDisabledForCaching);
    try {
      if (!Array.isArray(session.interactionLogs)) {
        throw new Error(
          "session.interactionLogs is not an array or is undefined"
        );
      }
      const logIndex = session.interactionLogs.findIndex(
        (l) => l.timestamp === log.timestamp && l.prompt === log.prompt
      );
      if (logIndex === -1) throw new Error("Log not found in session");
      const updatedLogs = session.interactionLogs.map((l, idx) =>
        idx === logIndex
          ? { ...l, disabledForCaching: newDisabledForCaching }
          : l
      );

      const sessionRef = doc(customFirestore, "sessions", session.id);
      await updateDoc(sessionRef, { interactionLogs: updatedLogs });
    } catch (e) {
      setDisabledForCaching(log.disabledForCaching ?? false);
      console.error("Error in handleCheckboxChange:", e);
    } finally {
      setUpdating(false);
    }
  };

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const handleCopy = useCallback(
    async (text: string) => {
      navigator.clipboard.writeText(text).then((_) => {
        setCustomAlertState({
          open: true,
          type: "info",
          title: "Copied to clipboard",
          description: text,
        });
      });
    },
    [setCustomAlertState]
  );

  const removeCacheMutation = useMutation({
    mutationKey: [
      "removeCache",
      {
        ...(session.testId && { test_id: session.testId }),
        ...(log.stepNumber && { step_number: log.stepNumber }),
        prompt: log.prompt,
        device_name: session.deviceConfig.device,
        organisation_id: session.organisationId,
        screen_resolution: session.deviceConfig.screenResolution,
        platform: session.deviceConfig.platform,
      },
    ],
    mutationFn: async () => {
      await axios.post(`${FASTAPI_SERVER_URL}/sessions/remove_cache`, {
        ...(session.testId && { test_id: session.testId }),
        ...(log.stepNumber && { step_number: log.stepNumber }),
        prompt: log.prompt,
        device_name: session.deviceConfig.device,
        organisation_id: session.organisationId,
        screen_resolution: session.deviceConfig.screenResolution,
        platform: session.deviceConfig.platform,
      });
      setCustomAlertState({
        open: true,
        type: "success",
        title: "Success",
        description: `Successfully removed step from cache`,
      });
    },
    onError: (_) => {
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Something went wrong",
        description: "An error occurred removing step from cache",
      });
    },
  });

  const removeCacheHandler = useCallback(() => {
    removeCacheMutation.mutate();
  }, []);

  return (
    <LogItemWrapper>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ReasoningAndCommands
          shouldDisplayRemoveCacheButton={true}
          removeCacheHandler={
            !log.fromCache || log.disabledForCaching
              ? undefined
              : removeCacheHandler
          }
          isRemovingCache={removeCacheMutation.isPending}
          promptActionHandler={
            user.isAnonymous ? undefined : promptActionHandler
          }
          stepDescription={log.prompt}
          fromCache={log.fromCache}
          reasoning={log.reasoning}
          absoluteTimestamp={DateTime.fromISO(
            `${log.timestamp.slice(0, 23)}+02:00`
          )
            .toLocal()
            .toMillis()}
          commands={log.gptCommands.join("\n")}
          displayAnonymousTooltip={user.isAnonymous}
          assertionConditions={
            assertionConditions.length > 0 ? assertionConditions : undefined
          }
          assertionRetryNumber={assertionRetryNumber}
          isCheck={isCheckStep(log)}
          extraContent={
            healedLocator && healedLocator !== "None" ? (
              <HealedLocatorLine>
                <span className="healed-label">Healed with:</span>
                <span className="healed-value">{healedLocator}</span>
                <Button
                  variant="contained"
                  startIcon={<FileCopy />}
                  onClick={async (_) => {
                    await handleCopy(healedLocator);
                  }}
                >
                  Copy
                </Button>
              </HealedLocatorLine>
            ) : healedLocator ? (
              <ExceptionMessage>
                <span className="message-label">Self healing failed</span>
                <span className="message-value">
                  {session.interactionLogs.at(-1)?.gptCommands}
                </span>
              </ExceptionMessage>
            ) : undefined
          }
        />
      </div>
      <Screenshot
        setScreenshot={setScreenshot}
        nonAnnotatedImage={
          annotatedBaselineData.data?.nonAnnotatedScreenshot ??
          log.screenshotUrl
        }
        annotatedImage={annotatedBaselineData.data?.annotatedImage}
        isAnonymous={user.isAnonymous}
      />
    </LogItemWrapper>
  );
};

const Screenshot = ({
  annotatedImage,
  nonAnnotatedImage,
  setScreenshot,
  isAnonymous,
}: {
  annotatedImage?: string;
  nonAnnotatedImage: string;
  setScreenshot: any;
  isAnonymous: boolean;
}) => {
  return (
    <ScreenshotWrapper>
      {nonAnnotatedImage && (
        <Tooltip
          title={`Create a template${
            isAnonymous ? ". Logged in users only" : ""
          }`}
          placement="left"
        >
          <ActionButton disabled={isAnonymous} onClick={setScreenshot}>
            <CameraAlt />
          </ActionButton>
        </Tooltip>
      )}
      <img src={annotatedImage ?? nonAnnotatedImage} />
    </ScreenshotWrapper>
  );
};

const ActionButton = styled.button`
  position: absolute;
  left: -24px;
  color: white;
  padding: 16px;
  border-radius: 9999px;
  background-color: #4c63ff;

  &:disabled {
    color: #455a64;
    cursor: not-allowed;
    background-color: #bdbdbd;
  }

  &:hover {
    filter: drop-shadow(0 1px 2px rgb(0 0 0 / 0.1))
      drop-shadow(0 1px 1px rgb(0 0 0 / 0.06));
  }
`;

const ContentWrapper = styled.div`
  background-color: rgb(232, 232, 232);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 16px;
  gap: 16px;
`;

const ScreenshotWrapper = styled.div`
  grid-area: screenshot;
  position: relative;
`;

const LogItemWrapper = materialStyled(Paper)(() => ({
  display: "grid",
  gridTemplateAreas: '"info screenshot"',
  gridTemplateColumns: "1fr 25%",
  gap: "24px",
  padding: "16px",
}));

export default Content;
